/**
 * fileGlyphs — THE file/folder glyph + kind vocabulary for the Files explorer.
 *
 * Every surface in the explorer (the list, the recycle bin, the client-docs
 * embed) used to carry its own `getFileIcon` returning the same grey
 * `PiFileLight` for a PDF, a spreadsheet and a zip, plus its own copy of the
 * extension sniffing that feeds the Type badge. One vocabulary here means a
 * .docx looks like a .docx everywhere, and the glyph and the badge can never
 * disagree about what a row is.
 *
 * Colour is a semantic token (`text-fg-muted`) rather than a `text-gray-500`
 * pair, so the glyphs flip with the theme without a `dark:` variant.
 */

import React from 'react';
import {
  PiBriefcaseLight,
  PiFileAudioLight,
  PiFileCloudLight,
  PiFileCodeLight,
  PiFileCsvLight,
  PiFileDocLight,
  PiFileImageLight,
  PiFileLight,
  PiFileMagnifyingGlassLight,
  PiFilePdfLight,
  PiFilePptLight,
  PiFileTextLight,
  PiFileVideoLight,
  PiFileXlsLight,
  PiFileZipLight,
  PiFolderLight,
  PiFolderSimpleStarLight,
  PiFolderSimpleUserLight,
} from 'react-icons/pi';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';

/** The label shown in the Type badge, and the key the glyph is chosen by. */
export type FileKind =
  | 'PDF'
  | 'DOCX'
  | 'XLSX'
  | 'PPTX'
  | 'CSV'
  | 'TXT'
  | 'IMAGE'
  | 'VIDEO'
  | 'AUDIO'
  | 'ZIP'
  | 'CODE'
  | string;

/**
 * Classify a file from its name and (browser-reported, so secondary) MIME
 * type. Falls back to the bare uppercased extension, which reads better in
 * the badge than a generic "FILE".
 */
export const getFileKind = (fileName: string, mimeType?: string): FileKind => {
  const name = (fileName || '').toLowerCase();
  const mime = (mimeType || '').toLowerCase();
  const ends = (...exts: string[]) => exts.some((e) => name.endsWith(e));

  if (mime.includes('pdf') || ends('.pdf')) return 'PDF';
  if (ends('.csv')) return 'CSV';
  if (
    mime.includes('spreadsheet') ||
    mime.includes('sheet') ||
    mime.includes('excel') ||
    ends('.xlsx', '.xls')
  ) {
    return 'XLSX';
  }
  if (
    mime.includes('word') ||
    mime.includes('wordprocessingml') ||
    mime.includes('document') ||
    ends('.docx', '.doc', '.rtf', '.odt')
  ) {
    return 'DOCX';
  }
  if (mime.includes('presentation') || mime.includes('powerpoint') || ends('.pptx', '.ppt')) {
    return 'PPTX';
  }
  if (mime.startsWith('image/') || mime.includes('image') || ends('.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp', '.heic')) {
    return 'IMAGE';
  }
  if (mime.startsWith('video/') || ends('.mp4', '.mov', '.avi', '.mkv', '.webm')) return 'VIDEO';
  if (mime.startsWith('audio/') || ends('.mp3', '.wav', '.m4a', '.ogg')) return 'AUDIO';
  if (mime.includes('zip') || ends('.zip', '.rar', '.7z', '.tar', '.gz')) return 'ZIP';
  if (ends('.json', '.xml', '.yml', '.yaml', '.html', '.md')) return 'CODE';
  if (mime.includes('text') || ends('.txt')) return 'TXT';

  return name.split('.').pop()?.toUpperCase() || 'FILE';
};

const KIND_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  PDF: PiFilePdfLight,
  DOCX: PiFileDocLight,
  XLSX: PiFileXlsLight,
  PPTX: PiFilePptLight,
  CSV: PiFileCsvLight,
  TXT: PiFileTextLight,
  IMAGE: PiFileImageLight,
  VIDEO: PiFileVideoLight,
  AUDIO: PiFileAudioLight,
  ZIP: PiFileZipLight,
  CODE: PiFileCodeLight,
};

/** Type-badge colour per kind. Anything unclassified stays neutral. */
const KIND_BADGE: Record<string, BadgeColor> = {
  PDF: 'red',
  DOCX: 'blue',
  XLSX: 'green',
  PPTX: 'orange',
  CSV: 'emerald',
  TXT: 'gray',
  IMAGE: 'purple',
  VIDEO: 'indigo',
  AUDIO: 'cyan',
  ZIP: 'amber',
  CODE: 'slate',
};

export const getKindBadgeColor = (kind: FileKind): BadgeColor => KIND_BADGE[kind] || 'gray';

interface GlyphProps {
  /** Extra classes — sizing lives at the call site (`w-5 h-5` in a row). */
  className?: string;
}

/** The glyph for a file, chosen from its kind. */
export const FileGlyph: React.FC<GlyphProps & { fileName: string; mimeType?: string; category?: string }> = ({
  fileName,
  mimeType,
  category,
  className = 'w-5 h-5',
}) => {
  // A legal category, when the backend states one, is more informative than
  // the container format — a "contract" is worth its own glyph even as .docx.
  const cat = (category || '').toLowerCase();
  if (cat === 'legal document') return <PiFileMagnifyingGlassLight className={`${className} text-fg-muted`} />;

  const Icon = KIND_ICON[getFileKind(fileName, mimeType)] || PiFileLight;
  return <Icon className={`${className} text-fg-muted`} />;
};

/** The glyph for a folder — the well-known org folders get their own. */
export const FolderGlyph: React.FC<GlyphProps & { folderName?: string }> = ({
  folderName,
  className = 'w-5 h-5',
}) => {
  const name = (folderName || '').toLowerCase();
  const Icon =
    name === 'clients'
      ? PiFolderSimpleUserLight
      : name === 'matters' || name === 'cases'
      ? PiBriefcaseLight
      : name === 'shared'
      ? PiFileCloudLight
      : name === 'marketplace files'
      ? PiFolderSimpleStarLight
      : PiFolderLight;
  return <Icon className={`${className} text-fg-muted`} />;
};

export default FileGlyph;
